/**
 * The visitor's own IANA time zone, read from the browser.
 *
 * Hamla places delayed campaign steps inside the recipient's waking hours. A
 * contact's country can imply a zone only where the country has one — it says
 * nothing useful for the US, Brazil, Canada or Australia — so the browser's own
 * answer is what makes the send window work for everybody.
 *
 * Defensive on purpose: `Intl` is absent or throws in some embedded webviews
 * and older in-app browsers, and a missing zone must never break identify.
 */

/** The browser's IANA zone (e.g. "America/Sao_Paulo"), or undefined. */
export function browserTimeZone(): string | undefined {
  try {
    const zone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
    // Some runtimes report "UTC" as a stand-in when they don't actually know;
    // it is still a valid zone and better than nothing, so it is kept. Only
    // empty/non-string answers are discarded.
    return typeof zone === 'string' && zone.length > 0 ? zone : undefined;
  } catch {
    return undefined;
  }
}

/**
 * `profile` with the browser's time zone filled in, unless the caller supplied
 * one — an integrator passing a zone explicitly knows something we don't (a
 * server-rendered identify for a user whose account says otherwise).
 *
 * Returns undefined for an absent profile and no readable zone, so identify
 * keeps sending no `profile` key at all rather than an empty object.
 */
export function withBrowserTimeZone<T extends { timezone?: string }>(
  profile: T | undefined,
): (T & { timezone?: string }) | undefined {
  if (profile?.timezone) return profile;
  const timezone = browserTimeZone();
  if (!timezone) return profile;
  return { ...(profile ?? ({} as T)), timezone };
}
